import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface PaymentState {
  // Core payment state
  payingForResidentId: number;
  payingForLeaseId: number;
  payingWithSourceId: number;
  amountToPay: number;
  isCustomAmount: boolean;
  isPaymentModalOpen: boolean;
  
  // Associated lease account for paying on behalf of others
  associatedLeaseAccount?: {
    firstName?: string;
    lastName?: string;
    id?: number;
  };
  
  // Payment method validation state
  selectedPaymentMethod?: {
    id: string | number;
    sourceId: string | number;
    brand?: string;
    last4?: string;
    type?: string;
    name?: string;
    expiry?: string;
    expirationMonth?: number;
    expirationYear?: number;
    expMonth?: number;
    expYear?: number;
    blockedAt?: boolean;
    isDefault?: boolean;
    accttype?: string;
    isDebitCard?: boolean;
  };
  
  // Fee calculation state
  calculatedFees?: {
    fee: number;
    feeType: 'fixed' | 'percentage';
    total: number;
    convenienceFee: number;
  };
  
  // UI state
  currentWizardStep: number;
  isProcessingPayment: boolean;
  paymentError?: string;
  
  // Form validation state
  validationErrors: {
    [key: string]: string;
  };
}

const initialState: PaymentState = {
  payingForResidentId: 0,
  payingForLeaseId: 0,
  payingWithSourceId: 0,
  amountToPay: 0,
  isCustomAmount: false,
  isPaymentModalOpen: false,
  currentWizardStep: 0,
  isProcessingPayment: false,
  validationErrors: {},
};

const paymentsSlice = createSlice({
  name: 'payments',
  initialState,
  reducers: {
    // Core payment actions (matching web implementation)
    resetPayments: () => initialState,
    
    setPayingForResidentId: (state, action: PayloadAction<number>) => {
      state.payingForResidentId = action.payload;
    },
    
    setPayingForLeaseId: (state, action: PayloadAction<number>) => {
      state.payingForLeaseId = action.payload;
    },
    
    setPayingWithSourceId: (state, action: PayloadAction<number>) => {
      state.payingWithSourceId = action.payload;
    },
    
    setAmountToPay: (state, action: PayloadAction<number>) => {
      state.amountToPay = action.payload;
    },
    
    setIsCustomAmount: (state, action: PayloadAction<boolean>) => {
      state.isCustomAmount = action.payload;
    },
    
    setIsPaymentModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isPaymentModalOpen = action.payload;
    },
    
    setAssociatedLeaseAccount: (state, action: PayloadAction<PaymentState['associatedLeaseAccount']>) => {
      state.associatedLeaseAccount = action.payload;
    },
    
    // Payment method management
    setSelectedPaymentMethod: (state, action: PayloadAction<PaymentState['selectedPaymentMethod']>) => {
      state.selectedPaymentMethod = action.payload;
    },
    
    // Fee calculation
    setCalculatedFees: (state, action: PayloadAction<PaymentState['calculatedFees']>) => {
      state.calculatedFees = action.payload;
    },
    
    // Wizard navigation
    setCurrentWizardStep: (state, action: PayloadAction<number>) => {
      state.currentWizardStep = action.payload;
    },
    
    nextWizardStep: (state) => {
      state.currentWizardStep += 1;
    },
    
    previousWizardStep: (state) => {
      if (state.currentWizardStep > 0) {
        state.currentWizardStep -= 1;
      }
    },
    
    // Processing state
    setIsProcessingPayment: (state, action: PayloadAction<boolean>) => {
      state.isProcessingPayment = action.payload;
    },
    
    setPaymentError: (state, action: PayloadAction<string | undefined>) => {
      state.paymentError = action.payload;
    },
    
    // Validation
    setValidationError: (state, action: PayloadAction<{ field: string; error: string }>) => {
      state.validationErrors[action.payload.field] = action.payload.error;
    },
    
    clearValidationError: (state, action: PayloadAction<string>) => {
      delete state.validationErrors[action.payload];
    },
    
    clearAllValidationErrors: (state) => {
      state.validationErrors = {};
    },
    
    // Bulk update for form data
    updatePaymentForm: (state, action: PayloadAction<Partial<PaymentState>>) => {
      Object.assign(state, action.payload);
    },
  },
});

export const {
  resetPayments,
  setPayingForResidentId,
  setPayingForLeaseId,
  setPayingWithSourceId,
  setAmountToPay,
  setIsCustomAmount,
  setIsPaymentModalOpen,
  setAssociatedLeaseAccount,
  setSelectedPaymentMethod,
  setCalculatedFees,
  setCurrentWizardStep,
  nextWizardStep,
  previousWizardStep,
  setIsProcessingPayment,
  setPaymentError,
  setValidationError,
  clearValidationError,
  clearAllValidationErrors,
  updatePaymentForm,
} = paymentsSlice.actions;

export default paymentsSlice.reducer;
